import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./AbyssPaywall.module.scss"

/**
 * AbyssPaywall — an original, self-contained ONBOARDING paywall.
 *
 * Concept ("Abyss" — "The safest place on Earth is the hardest to reach"):
 * the first-run user is dropped into a slow cinematic descent into the deep
 * ocean. Sunlit god-rays fade overhead, bioluminescent marine snow drifts past,
 * a depth gauge counts down to −10,994 m (the bottom of the Mariana Trench), and
 * at the floor of the abyss rests a glowing VAULT ORB — a dark sphere with a warm
 * living core leaking light through its seam, breathing, with the user's memories
 * (photos) sealed inside softly-bobbing portholes around it. The metaphor is
 * literal and calming: your whole world, kept where nothing on Earth can reach it.
 *
 * It reframes a privacy purchase as awe and safety rather than fear. The valence
 * is the OPPOSITE of a loss-aversion screen: the deep is the good, safe place —
 * there is no threat, no countdown to doom, no implication that anything is ever
 * lost. It is a welcome.
 *
 * The whole layout funnels to the YEARLY plan: the annual "A year in the deep"
 * card is the only large, glowing, tappable surface (selected by default), its
 * annual price is the single biggest number on screen (with a tiny per-month
 * equivalent beneath, so the commitment feels small and it stays App Store
 * compliant — the annual price is the most prominent price for the annual
 * product), a 7-day free-trial frames the entry, the yearly savings is shouted,
 * and monthly is demoted to one quiet "surface rate" line of text.
 *
 * Fully self-contained: no shared paywall components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data. Reuses the
 * brand fonts (RedHatDisplay + KronaOne for the depth gauge / title accent), the
 * brand primary blue as the bioluminescence, the brand secondary gold as the warm
 * vault core, and existing repo feature icons for the perk list.
 */

const PUBLIC = process.env.PUBLIC_URL ?? ""

// User memories sealed in the portholes around the vault orb. Bundled locally so
// they render offline and on the live site; each has a gradient onError fallback.
const MEMORIES = [
  { src: `${PUBLIC}/img/abyss/memory1.jpg`, fallback: "#1c4a86" },
  { src: `${PUBLIC}/img/abyss/memory3.jpg`, fallback: "#123a6e" },
  { src: `${PUBLIC}/img/abyss/memory2.jpg`, fallback: "#0f315e" },
  { src: `${PUBLIC}/img/abyss/memory4.jpg`, fallback: "#15407a" },
  { src: `${PUBLIC}/img/abyss/memory5.jpg`, fallback: "#0d2a52" },
]

const SNOW_COUNT = 46
const TARGET_DEPTH = 10994 // metres — the floor of the Mariana Trench

const AbyssPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData
  const reduceMotion = useReducedMotion()

  const [showClose, setShowClose] = useState(false)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#02060f")
    actions.setFooterInsetColor("#01040a")
    const t = setTimeout(() => setShowClose(true), 2600)
    return () => clearTimeout(t)
  }, [actions])

  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const hasTrial = yearlyProduct?.hasTrial ?? true

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined =
      selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.screen}>
      <OceanScene reduceMotion={!!reduceMotion} />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.5, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 0.9 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 16, paddingBottom: bottomInset + 14 }}
      >
        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            PRIVATE BY NATURE
          </span>
          <h1 className={styles.title}>
            The safest place on Earth
            <br />
            is the <span className={styles.titleAccent}>hardest to reach.</span>
          </h1>
        </motion.div>

        <VaultOrb reduceMotion={!!reduceMotion} />

        <motion.p
          className={styles.subtitle}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
        >
          Your photos live encrypted in the deep — pitch black, end-to-end,
          eleven kilometres down. Only you can surface them.
        </motion.p>

        <PerkList />

        <motion.div
          className={styles.plans}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
        >
          <button
            className={`${styles.yearCard} ${
              selected === "yearly" ? styles.yearCardActive : ""
            }`}
            onClick={() => setSelected("yearly")}
            aria-pressed={selected === "yearly"}
          >
            <span className={styles.cardCurrent} aria-hidden />
            <span className={styles.bestRibbon}>SAVE {savings}%</span>

            <div className={styles.yearHead}>
              <span
                className={`${styles.radio} ${
                  selected === "yearly" ? styles.radioOn : ""
                }`}
                aria-hidden
              >
                {selected === "yearly" && <Check />}
              </span>
              <div className={styles.yearHeadText}>
                <span className={styles.yearName}>A year in the deep</span>
                <span className={styles.yearSub}>
                  {hasTrial
                    ? "7 days free, then billed yearly"
                    : "Full protection, billed yearly"}
                </span>
              </div>
            </div>

            <div className={styles.priceBlock}>
              <div className={styles.priceRow}>
                <span className={styles.priceMain}>{yearlyPrice}</span>
                <span className={styles.pricePer}>/year</span>
              </div>
              <div className={styles.priceSub}>
                just {perMonthEquivalent}/mo · billed annually
              </div>
            </div>
          </button>

          <button
            className={`${styles.monthlyLine} ${
              selected === "monthly" ? styles.monthlyLineOn : ""
            }`}
            onClick={() => setSelected("monthly")}
            aria-pressed={selected === "monthly"}
          >
            Prefer monthly? {monthlyPrice}/mo — surface rate
          </button>
        </motion.div>

        <CtaButton
          reduceMotion={!!reduceMotion}
          label={
            selected === "yearly"
              ? hasTrial
                ? "Start free — descend now"
                : "Descend now"
              : "Continue monthly"
          }
          onClick={handlePurchase}
        />

        <motion.p
          className={styles.ctaNote}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.6 }}
        >
          Surfaces instantly · Cancel anytime in one tap
        </motion.p>

        <TrustRow />

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Backdrop: a vertical descent into the deep ocean — fading god-rays   */
/* from the surface overhead, drifting bioluminescent marine snow, a    */
/* caustic floor glow, and a pointer-driven parallax current.           */
/* ------------------------------------------------------------------ */
const OceanScene = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const sceneRef = useRef<HTMLDivElement>(null)

  const snow = useMemo(
    () =>
      Array.from({ length: SNOW_COUNT }, (_, i) => {
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        const r4 = Math.abs(Math.sin(i * 33.77) * 5512.19) % 1
        const gold = r4 > 0.86
        return {
          left: `${r1 * 100}%`,
          size: 1.4 + r3 * 3.4,
          delay: `${r2 * 14}s`,
          dur: `${13 + r1 * 16}s`,
          drift: `${(r3 - 0.5) * 64}px`,
          gold,
          glow: 0.35 + r3 * 0.5,
        }
      }),
    [],
  )

  useEffect(() => {
    if (reduceMotion) return
    const scene = sceneRef.current
    if (!scene) return
    const onMove = (e: PointerEvent) => {
      const r = scene.getBoundingClientRect()
      const x = (e.clientX - r.left) / r.width - 0.5
      const y = (e.clientY - r.top) / r.height - 0.5
      scene.style.setProperty("--px", `${(x * 26).toFixed(1)}px`)
      scene.style.setProperty("--py", `${(y * 18).toFixed(1)}px`)
    }
    const onLeave = () => {
      scene.style.setProperty("--px", "0px")
      scene.style.setProperty("--py", "0px")
    }
    scene.addEventListener("pointermove", onMove)
    scene.addEventListener("pointerleave", onLeave)
    return () => {
      scene.removeEventListener("pointermove", onMove)
      scene.removeEventListener("pointerleave", onLeave)
    }
  }, [reduceMotion])

  return (
    <div className={styles.scene} ref={sceneRef} aria-hidden>
      <div className={styles.water} />
      <div className={`${styles.rays} ${reduceMotion ? styles.raysStill : ""}`}>
        <span style={{ left: "18%" }} />
        <span style={{ left: "42%" }} />
        <span style={{ left: "63%" }} />
        <span style={{ left: "82%" }} />
      </div>
      <div className={styles.surfaceShimmer} />
      {!reduceMotion && (
        <div className={styles.snow}>
          {snow.map((s, i) => (
            <span
              key={i}
              className={`${styles.flake} ${s.gold ? styles.flakeGold : ""}`}
              style={
                {
                  left: s.left,
                  width: s.size,
                  height: s.size,
                  opacity: s.glow,
                  animationDelay: s.delay,
                  animationDuration: s.dur,
                  "--drift": s.drift,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div className={styles.floorGlow} />
      <div className={styles.vignette} />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The hero: a glowing vault orb resting on the abyssal floor, breathing */
/* with a warm core, ringed by bobbing memory portholes, with a depth    */
/* gauge that counts down to the floor of the trench.                    */
/* ------------------------------------------------------------------ */
const VaultOrb = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const [depth, setDepth] = useState(reduceMotion ? TARGET_DEPTH : 0)

  // One-time "arrival" count to the trench floor — a reveal, not a threat.
  useEffect(() => {
    if (reduceMotion) return
    let raf = 0
    let start = 0
    const DURATION = 2200
    const step = (t: number) => {
      if (!start) start = t
      const p = Math.min(1, (t - start) / DURATION)
      const eased = 1 - Math.pow(1 - p, 3)
      setDepth(Math.round(eased * TARGET_DEPTH))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [reduceMotion])

  // Portholes arranged in an arc cradling the top of the orb.
  const ring = useMemo(() => {
    const n = MEMORIES.length
    return MEMORIES.map((m, i) => {
      const angle = -143 + (i * 106) / (n - 1) // arc cradling the orb's top
      const rad = (angle * Math.PI) / 180
      const radius = 120
      return {
        ...m,
        x: Math.cos(rad) * radius,
        y: Math.sin(rad) * radius * 0.92,
        delay: i * 0.12,
        bob: 3 + (i % 3),
        bobDur: `${4.5 + (i % 4) * 0.8}s`,
      }
    })
  }, [])

  return (
    <motion.div
      className={styles.orbStage}
      initial={{ opacity: 0, scale: 0.9, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 0.34, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={styles.orbHalo} aria-hidden />
      <div className={styles.caustics} aria-hidden />

      {ring.map((p, i) => (
        <motion.div
          key={i}
          className={styles.porthole}
          style={
            {
              left: `calc(50% + ${p.x}px)`,
              top: `calc(50% + ${p.y}px)`,
              "--bob": `${p.bob}px`,
              "--bobdur": p.bobDur,
            } as React.CSSProperties
          }
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: 0.7 + p.delay,
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <span
            className={`${styles.portholeInner} ${
              reduceMotion ? "" : styles.portholeBob
            }`}
          >
            <img
              src={p.src}
              alt=""
              onError={(e) => {
                const t = e.currentTarget
                t.style.display = "none"
                ;(t.parentElement as HTMLElement).style.background = p.fallback
              }}
            />
            <span className={styles.portholeGlass} />
            <span className={styles.portholeRim} />
          </span>
        </motion.div>
      ))}

      <div className={`${styles.orb} ${reduceMotion ? styles.orbStill : ""}`}>
        <div className={styles.orbBody} />
        <div className={styles.orbSeam} />
        <div className={styles.orbCore} />
        <div className={styles.orbSpecular} />
        <div className={styles.orbLock}>
          <LockGlyph />
        </div>
      </div>

      <div className={styles.gauge}>
        <span className={styles.gaugeValue}>
          −{depth.toLocaleString()}
          <span className={styles.gaugeUnit}>m</span>
        </span>
        <span className={styles.gaugeLabel}>SEALED ON THE TRENCH FLOOR</span>
      </div>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/* What lives down here with you — perks using repo feature icons.      */
/* ------------------------------------------------------------------ */
const PERKS = [
  {
    Icon: svg.featureIcons.CloudVault,
    text: "Unlimited end-to-end encrypted vault",
  },
  {
    Icon: svg.featureIcons.Breakin,
    text: "Break-in alerts catch anyone who tries",
  },
  { Icon: svg.featureIcons.Decoy, text: "Decoy vault & disguised app icon" },
  {
    Icon: svg.featureIcons.FacedownLock,
    text: "Auto-locks the instant you look away",
  },
]

const PerkList = () => {
  return (
    <motion.div
      className={styles.perks}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.95, duration: 0.6 }}
    >
      {PERKS.map((p, i) => (
        <motion.div
          key={i}
          className={styles.perk}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.98 + i * 0.07, duration: 0.45 }}
        >
          <span className={styles.perkIcon}>
            <p.Icon />
          </span>
          <span className={styles.perkText}>{p.text}</span>
          <span className={styles.perkCheck}>
            <Check />
          </span>
        </motion.div>
      ))}
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/* CTA — a bioluminescent button that shimmers and gently bobs, and     */
/* releases a trail of bubbles when pressed.                            */
/* ------------------------------------------------------------------ */
const CtaButton = ({
  reduceMotion,
  label,
  onClick,
}: {
  reduceMotion: boolean
  label: string
  onClick: () => void
}) => {
  const [bubbles, setBubbles] = useState<{ id: number; left: number }[]>([])
  const idRef = useRef(0)

  const handle = useCallback(() => {
    if (!reduceMotion) {
      const burst = Array.from({ length: 7 }, () => ({
        id: idRef.current++,
        left: 18 + Math.abs(Math.sin(idRef.current * 9.7) * 64),
      }))
      setBubbles((b) => [...b, ...burst])
      setTimeout(() => {
        setBubbles((b) => b.slice(burst.length))
      }, 1100)
    }
    onClick()
  }, [onClick, reduceMotion])

  return (
    <motion.div
      className={styles.ctaWrap}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.12, duration: 0.6 }}
    >
      <button
        className={`${styles.cta} ${reduceMotion ? styles.ctaStill : ""}`}
        onClick={handle}
      >
        <span className={styles.ctaShimmer} aria-hidden />
        <span className={styles.ctaLabel}>{label}</span>
        <span className={styles.ctaArrow}>↓</span>
        <span className={styles.bubbles} aria-hidden>
          {bubbles.map((b) => (
            <span
              key={b.id}
              className={styles.bubble}
              style={{ left: `${b.left}%` }}
            />
          ))}
        </span>
      </button>
    </motion.div>
  )
}

const TrustRow = () => {
  return (
    <motion.div
      className={styles.trust}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.35, duration: 0.6 }}
    >
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>4.8★</span>
        <span className={styles.trustLabel}>App Store</span>
      </div>
      <span className={styles.trustSep} />
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>10M+</span>
        <span className={styles.trustLabel}>vaults sealed</span>
      </div>
      <span className={styles.trustSep} />
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>Zero-knowledge</span>
        <span className={styles.trustLabel}>only you hold the key</span>
      </div>
    </motion.div>
  )
}

/* ---- inline glyphs ---- */
const LockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <rect
      x="4.5"
      y="10.5"
      width="15"
      height="10.5"
      rx="2.6"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M7.5 10.5V7.8a4.5 4.5 0 0 1 9 0v2.7"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.4" r="1.5" fill="currentColor" />
  </svg>
)

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default AbyssPaywall
